import { Handle, Position } from '@xyflow/react'
import { selectedBorder, activeBorder } from './selectedBorder.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'

/**
 * Node identity color. Exported so the editor's palette entry reads it from
 * here rather than repeating the literal: node-category colors are functional
 * (they encode category, never the theme), and this directory is where the
 * palette-enforcement lock expects them to live.
 */
export const RIG_CONTROLLER_COLOR = '#e0a030'

/**
 * RigControllerNode — manual face-rig control source (ENG-946 rig preview).
 *
 * Wire this node and the face graph owns the routing. Strength comes from the
 * parent blend path (sentinel `__rigcontrol__` weight in the evaluator), so
 * wiring this through a Blend fades the manual pose against LiveLink or a clip
 * like any other source — and that authored weight outranks the panel's own
 * direct drive (ENG-1027), which only fills in for a graph with no such node.
 *
 *   passthrough = false — the sliders ARE the pose.
 *   passthrough = true  — the live A2F ARKit stream flows through this node,
 *                         and channels locked in the panel override it.
 *
 * Internal sentinel: data.clipName = '__rigcontrol__'
 */
export function RigControllerNode({ data, selected }) {
  const weight      = data._debugWeight ?? 0
  const active      = weight > 0.001
  const passthrough = data.passthrough === true

  return (
    <div style={{
      background: selected ? '#20160f' : '#181009',
      border: `2px solid ${selected ? selectedBorder('#504018') : active ? activeBorder('#504018') : '#504018'}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 195,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 15 }}>🎚️</span>
        <span style={{ color: '#d0a040', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>RIG CONTROLLER</span>
        {active
          ? <span style={{ color: '#e0a030', fontSize: 9, marginLeft: 'auto', letterSpacing: 0.3 }}>
              ● {(weight * 100).toFixed(0)}%
            </span>
          : <span style={{ color: '#504030', fontSize: 9, marginLeft: 'auto' }}>○ idle</span>}
      </div>

      {/* Source label */}
      <div style={{ color: '#8a7040', fontSize: 10, marginBottom: 8 }}>
        ARKit · 52 controls · {passthrough ? 'A2F passthrough' : 'slider pose'}
      </div>

      {/* Mode */}
      <label
        onMouseDown={e => e.stopPropagation()}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, fontSize: 10,
          color: '#a08858', marginBottom: 8, cursor: 'pointer', userSelect: 'none',
        }}
      >
        <input className={nodeControlClass()}
          type="checkbox"
          checked={passthrough}
          onChange={e => data.onChange?.('passthrough', e.target.checked)}
          onMouseDown={e => e.stopPropagation()}
        />
        LiveLink passthrough
      </label>

      {/* Open the control board */}
      <button
        type="button"
        data-testid="rig-node-open-panel"
        onMouseDown={e => e.stopPropagation()}
        onClick={() => window.__RIG_PREVIEW__?.open()}
        style={{
          font: 'inherit', fontSize: 10, cursor: 'pointer', width: '100%',
          padding: '4px 6px', borderRadius: 4, border: '1px solid #504018',
          background: '#241a0c', color: '#d0a040', marginBottom: 8,
        }}
      >Open Rig Preview</button>

      {/* Weight bar */}
      <div style={{ height: 3, background: '#1a140a', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(weight, 1) * 100}%`,
          height: '100%',
          background: active ? '#e0a030' : '#504018',
          transition: 'width 0.1s linear',
        }} />
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="anim"
        style={{ background: active ? '#e0a030' : '#504018', width: 10, height: 10 }}
      />
    </div>
  )
}
